/* =====================================================================
 * B2C auth provider glue.
 *
 * The tenant manifest (`@auw/tenant`) describes the auth surface
 * declaratively (ids, kinds, label keys); the manifest cannot carry
 * function references because it needs to stay JSON-serialisable for
 * the `/config.json` runtime overlay. This helper pairs each manifest
 * entry with the concrete callback suite from `@auw/api-client`, then
 * hands the result to `<AuthProviderRegistry providers={...}>` in
 * `main.tsx`.
 *
 * B2C currently ships only the Google OAuth adapter; SAML / OIDC /
 * email+password entries in a future manifest would be silently
 * skipped by this helper until their adapters ship in Phase 10+.
 * Enterprise apps in `apps/enterprise` (Phase 10) will own their own
 * equivalent file with SAML/OIDC/email wiring.
 * ===================================================================== */

import {
  googleOAuthProvider,
  type AuthProvider,
  type AuthSessionResult,
  type DesktopAuthSessionResult,
} from '@auw/auth';
import type { TenantManifest } from '@auw/tenant';
import {
  buildGoogleLoginUrl,
  exchangeAuthCode,
  exchangeAuthCodeForDesktop,
} from '@auw/api-client';

/**
 * B2C's `api/client` historically exposes snake_case fields
 * (`access_token`, `expires_in`, `is_new_user`) that mirror the
 * backend payload verbatim. The `@auw/auth` contract uses camelCase
 * so every future adapter (SAML / OIDC / on-prem) converges on a
 * single shape. These tiny adapters do the field-name translation
 * at the one seam the B2C product currently needs.
 */
async function exchangeAuthCodeCamelCase(
  code: string,
): Promise<AuthSessionResult> {
  const response = await exchangeAuthCode(code);
  return {
    accessToken: response.access_token,
    expiresIn: response.expires_in,
    isNewUser: response.is_new_user,
  };
}

async function exchangeAuthCodeForDesktopCamelCase(
  code: string,
): Promise<DesktopAuthSessionResult> {
  const response = await exchangeAuthCodeForDesktop(code);
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    expiresIn: response.expires_in,
    isNewUser: response.is_new_user,
  };
}

/**
 * Materialise runtime `AuthProvider` instances from the tenant's
 * declarative `auth.providers` list.
 *
 * NotImplemented kinds (saml / oidc / email_password) are dropped on
 * B2C because the public product never advertises them; enterprise
 * tenants that genuinely want disabled stub buttons should ship
 * their own variant of this helper. Matching on both `kind` and
 * `id` keeps the exhaustiveness check honest — adding a new kind to
 * `@auw/auth` forces us to decide here whether B2C cares.
 */
export function buildTenantAuthProviders(
  tenant: TenantManifest,
): AuthProvider[] {
  const entries = tenant.auth?.providers ?? [];
  const out: AuthProvider[] = [];

  for (const entry of entries) {
    switch (entry.kind) {
      case 'google_oauth':
        out.push(
          googleOAuthProvider({
            kind: 'google_oauth',
            id: entry.id,
            labelKey: entry.labelKey,
            iconSrc: entry.iconSrc,
            buildLoginUrl: buildGoogleLoginUrl,
            exchangeAuthCode: exchangeAuthCodeCamelCase,
            exchangeAuthCodeForDesktop: exchangeAuthCodeForDesktopCamelCase,
          }),
        );
        break;
      case 'saml':
      case 'oidc':
      case 'email_password':
        break;
      case 'knox':
        // Knox SSO ships with the enterprise build (Samsung) only —
        // the b2c bundle ignores the entry rather than pulling in
        // the enterprise auth client. The case is enumerated here so
        // the exhaustiveness check below stays meaningful when
        // ``@auw/auth`` adds the next provider kind.
        break;
      default: {
        const exhaustiveCheck: never = entry.kind;
        void exhaustiveCheck;
        break;
      }
    }
  }

  return out;
}
